<template>
    <div class="overflow-x-hidden relative rounded-md">
      <!-- Border -->
      <div
        class="absolute top-0 left-0 w-full rounded-md border border-border-normal z-10 pointer-events-none select-none"
        :style="borderContainerStyle" />
    <!-- Query loader -->
    <div v-if="loading" class="bg-gray-100 opacity-50 rounded-md absolute top-0 left-0 w-full h-full z-50 flex justify-center items-center">
      <BaseLoadingSpinnerCircle class="text-icon-normal w-8 h-8"/>
    </div>
    <div class="w-full overflow-x-auto scroll-container">
      <table class="relative">
        <thead>
          <tr> 
            <!-- Grouped by column -->
            <th class="sticky-column">
              <div class="group-cell flex items-center px-2 py-3 h-10 gap-3 ">
                <input
                  type="checkbox"
                  class="checkbox-input"
                  :checked="preventRowAdd"
                  @change="toggleAllRows"
                  :disabled="graphInfo.selectedGraph === 'line'"
                >
                <BaseText
                  type="heading"
                  size="overline"
                  class="text-text-subdued uppercase"
                >
                  {{ groupedBy?.replace(/_/g, ' ') }}
                </BaseText>
                <BaseText v-if="selectedRows.length > 0" type="heading" size="overline" class="text-text-disabled">({{ selectedRows.length }} selected)</BaseText>
              </div>
            </th>
            <!-- Other selected kpis -->
            <th
              v-for="header in tableKpis"
              :key="header.key"
              :class="{ 'sticky-column': header.is_pinned, 'header-column': true }"
              :style="header.is_pinned ? { left: `${calculatePinOffset(header)}px` } : {}"
            >
            <HeaderActionCell 
              :disableActions="['status', 'creation_date_range'].includes(header.key)"
              :header="header"
              :isSelected="selectedColumns.includes(header.key)"
              :columnColor="graphInfo.selectedGraph === 'bar' ? getColumnColor(header) : ''"
              :currentSort="currentSort"
              :disableSelect="graphInfo.selectedGraph === 'line'"
              @pin="pinColumn" 
              @sort="sortColumn" 
              @select="selectColumn" 
            />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="`${row}-${index}`"
          >
            <td
              class="sticky-column table-cell"
              :class="{ 'table-highlight': hoveredCell.row === index }"
            >
              <div class="group-cell grid grid-cols-12 py-1 px-2 gap-x-3 items-center">
                <!-- Left checkbox -->
                <div class="col-span-1">
                  <input
                    :checked="isRowSelected(row.group_by)"
                    class="checkbox-input"
                    type="checkbox"
                    :disabled="(!isRowSelected(row.group_by) && preventRowAdd) || graphInfo.selectedGraph === 'line'"
                    @change="selectRow(row)"
                    :style="{ backgroundColor: isRowSelected(row.group_by || row.ad_id) ? getRowCheckboxColor(row.group_by) : '' }"
                  >
                </div>
                <!-- Middle info -->
                <div class="col-span-11 ml-1">
                  <div class="flex items-center flex-nowrap gap-2 min-w-0 ">
                    <!-- Group image -->
                    <img
                      :src="row.group_img_url || row?.ads?.[0]?.ad_image_url"
                      class="rounded-md w-7 h-7 flex-shrink-0 bg-cover"
                    >
                    <!-- Group title & ad count -->
                    <div class="flex-grow min-w-0 whitespace-nowrap relative">
                      <BaseText
                        v-clipboard:copy="row.group_by"
                        v-clipboard:success="handleCopySuccess"
                        class="text-text-muted truncate truncate-group-trigger cursor-pointer max-w-min"
                        size="sm"
                      >
                        {{ row?.group_by }}
                      </BaseText>
                      <div class="truncate-group-tooltip transform px-1.5 py-1 rounded-md absolute shadow-md space-y-1">
                        <BaseText class="text-white truncate" size="xs">{{ row?.group_by }}</BaseText>
                      </div>
                      <BaseText
                        class="text-text-normal"
                        size="xs"
                      >
                        {{ row.ads?.length }} Ads
                      </BaseText>
                    </div>
                  </div>
                </div>
                <!-- CTA actions -->
                <!-- <div class="col-span-2">
                  <div class="flex items-center min-w-0">
                    <div class="p-1 cursor-pointer">
                      <AdDetailsAnimatedIcon class="cursor-pointer text-icon-normal" />
                    </div>
                    <div class="p-1 cursor-pointer">
                      <CreativeInsightIcon class="cursor-pointer text-icon-normal" />
                    </div>
                  </div>
                </div> -->
              </div>
            </td>
            <!-- Selected KPI Columns -->
            <td
              v-for="(kpi, colIndex) in tableKpis"
              :key="`${kpi}-${colIndex}`"
              :ref="`td-${kpi.key}`"
              class="table-cell "
              :class="{ 'table-highlight': hoveredCell.col === colIndex || hoveredCell.row === index , 'sticky-column': kpi.is_pinned}"
              :style="[kpi.is_pinned ? {left:`${calculatePinOffset(kpi)}px`} : null, row?.[kpi.key] !== 0 ? {backgroundColor: getColoredCell(row,kpi)} : null]"
              @mouseover="hoveredCell = { col: colIndex, row: index }"
              @mouseleave="hoveredCell = {}"
            >
            <div class="cell relative ">
                <!-- Handle status & tag cells -->
                <StatusCell
                  class="px-3 py-2.5"
                  v-if="kpi.key === 'status' && tableConfig?.showStatus"
                  :data="{ value: { active: row.active_ad_count, paused: row.paused_ad_count } }"
                />
                <CreationDateRangeCell 
                  v-else-if="kpi?.key === 'creation_date_range'" 
                  :dateRange="{ start: row?.overall_min_ad_created_at, end: row?.overall_max_ad_created_at }" 
                />
                <TableCell
                  v-else
                  class="p-3"
                  :metric-direction="getMetricLookup?.[kpi.key]?.metric_direction"
                  :avg-diff="row?.percent_deviation?.[kpi.key]"
                  :cell-type="getMetricLookup?.[kpi.key]?.type"
                  :cell-value="row?.[kpi.key]"
                  :currency="lensCurrency"
                />
              </div>
            </td>
          </tr>
        </tbody>
        <!-- Table footer (Net results across all groupings) -->
        <tfoot v-if="netResults?.weighted_averages && netResults?.mean_averages">
            <tr>
              <td class="sticky-column">
                <div class="group-cell py-3 px-1.5 bg-background-disabled h-full flex gap-3 items-center">
                  <div class="relative group" @click="navToNetInfo"
                    @mouseenter="showTooltip('checkboxInclude', true, $event, 'Click here to learn more')"
                    @mouseleave="showTooltip('checkboxInclude', false, $event)">
                    <InformationIcon class="text-icon-disabled group-hover:text-icon-normal cursor-pointer" />
                  </div>
                  <div>
                    <BaseText size="sm" type="label" class="text-text-muted col-span-10">
                      Net Results
                    </BaseText>
                  </div>
                </div>
              </td>
              <td
                v-for="(kpi,kpiIndex) in tableKpis"
                :key="`net-${kpi}-${kpiIndex}`"
                :class="{ 'sticky-column': kpi.is_pinned }"
                :style="kpi.is_pinned ? { left: `${calculatePinOffset(kpi)}px` } : ''"
              >
                <div v-if="!kpi.is_metadata" class="cell flex items-center gap-3 bg-background-disabled" style="height:45px" :class="kpi.key === 'status' ? 'p-2.5' : 'p-3'">
                  <div v-if="kpi.key !== 'status'">
                    <FooterCell  :kpi="kpi?.key" :kpi-type="getKpiType(kpi)" :net-kpi="formatNumber(netResults?.sums?.[kpi?.key], getMetricLookup?.[kpi.key]?.type, lensCurrency)" :weighted-avg-value="formatNumber(netResults.weighted_averages?.[kpi.key], getMetricLookup?.[kpi.key]?.type, lensCurrency)" :mean-avg-value="formatNumber(netResults.mean_averages?.[kpi.key], getMetricLookup?.[kpi.key]?.type, lensCurrency)" :isAdLevel="isAdLevel"/>
                  </div>
                  <div v-else-if="kpi.key === 'status'" class="flex items-center gap-1 overflow-hidden">
                    <StatusCell :data="{value: {state: 'ACTIVE'}}" is-ad-level :overrideStatus="`${netResults.active_ad_count}`"/>
                    <StatusCell :data="{value: {state: 'PAUSED'}}" is-ad-level :overrideStatus="`${netResults.paused_ad_count}`"/>
                  </div>
                </div>
                <!-- If it's an ad-level metadata column, we display the number of unique values -->
                <div v-else class="cell p-3 bg-background-disabled">
                  <BaseText size="sm" class="text-text-muted" style="max-width: 132px">
                    {{ getMetadataNetResultLabel(kpi) }}
                  </BaseText>
                </div>
              </td>
            </tr>
          </tfoot>
      </table>
    </div>
    <!-- <TablePagination
      class="mr-12 ml-2 mb-1"
      v-if="tablePagination?.totalPages !== 0"
      :showing="rows.length"
      :tablePagination="tablePagination"
      @updatePagination="$event => $emit('paginationChanged', { type: null, value: $event})"
    /> -->
    <portal-target name="sort-dropdown">
      <!-- Sort dropdown -->
    </portal-target>
    <!-- Modals & Global tooltips -->
    <!-- <ColumnTooltips ref="tooltipContainer" :graphInfo="graphInfo"/> -->
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import kpiColors from '../../../utils/lens/selectedMetricColors'
import {getRelativeMetricScoreColor} from "../../../utils/lens/metricScoreUtil"
import { formatNumberLabel } from '../../../utils/lens/formatNumberLabel'

import ColumnTooltips from './ColumnTooltips.vue'
// Table cell components
import TablePagination from './TablePagination.vue'
import TableCell from './cells/TableCell.vue'
import StatusCell from './cells/StatusCell.vue'
import CreationDateRangeCell from './cells/CreationDateRangeCell.vue'
import HeaderActionCell from './cells/HeaderActionCell.vue'
import FooterCell from './cells/FooterCell.vue'

// Icons
import AdDetailsAnimatedIcon from '../../globals/Icons/AdDetailsAnimatedIcon.vue'
import SortColumnIcon from '../../globals/Icons/SortColumnIcon.vue'
import CheckmarkIcon from '../../globals/Icons/CheckmarkIcon.vue'
import PinIcon from '../../globals/Icons/SpyderV2Icons/PinIcon.vue'
import InformationIcon from '../../globals/Icons/InformationIcon.vue'
import BarGraphIcon from "../../globals/Icons/LensIcons/graphs/BarGraphIcon.vue"

export default {
  name: 'LensMetricTable',
  components: {
    TablePagination,
    TableCell,
    StatusCell,
    CreationDateRangeCell,
    HeaderActionCell,
    FooterCell,
    ColumnTooltips,

    AdDetailsAnimatedIcon,
    InformationIcon,
    SortColumnIcon,
    CheckmarkIcon,
    BarGraphIcon,
    PinIcon
  },
  props: {
    groupedBy: {
      type: String,
      default: () => 'Ad Name'
    },
    data: {
      type: Object,
      default: () => {}
    },
    selectedColumns: {
      type: Array,
      default: () => []
    },
    selectedRows: {
      type: Array,
      default: () => []
    },
    tableColumns: {
      type: Array,
      default: () => []
    },
    tableConfig: {
      type: Object,
      default: () => {}
    },
    currentSort: {
      type: Object,
      default: () => {}
    },
    maxRowCount: {
      type: Number,
      default: () => 8
    },
    summaryStats: {
      type: Object,
      default: () => {}
    },
    graphInfo: {
      type: Object,
      default: () => {}
    },
    loading: {
      type: Boolean,
      default: () => false
    },
    lensCurrency: {
      type: String,
      default: () => 'USD'
    },
    netResults: {
      type: Object,
      default: () => {}
    }
  },
  data () {

    const positiveCells  = ['cell-green-lowAvg', 'cell-green-midAvg', 'cell-green-highAvg']
    const negativeCells = ['cell-red-lowAvg', 'cell-red-midAvg', 'cell-red-highAvg']
    
    return {
      positiveCells,
      negativeCells,

      isAdLevel: false,
      hoveredCell: {}
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getMappedRowColor']),
    ...mapGetters('MetricsModule', ['getMetricLookup', 'getAppliedPreset']),
    borderContainerStyle() {
      return {
        height: `calc(100% - ${0}px)`
      }
    },
    rows () {
      if (this.data?.ad_groups?.length > 0) {
        return this.data.ad_groups
      }
      return []
    },
    pinnedColumns(){
      return this.tableColumns.filter((col) => col.is_pinned);
    },
    tableKpis () {
      const showStatus = this.tableConfig?.showStatus
      const showCreationRange = this.tableConfig?.showCreationRange && !this.isAdLevel

      if (this.pinnedColumns.length > 0) {
        return [
          ...(showStatus ? [{key: 'status'}] : []),
          ...(showCreationRange ? [{ key: 'creation_date_range', name: 'Creation Date Range' }] : []),
          ...[...this.tableColumns].sort((a, b) => {
            const indexA = this.pinnedColumns.indexOf(a)
            const indexB = this.pinnedColumns.indexOf(b)
            if (indexA === -1 && indexB === -1) return 0
            if (indexA === -1) return 1
            if (indexB === -1) return -1
            return indexA - indexB
          })
        ]
      }

      return [
        ...(showStatus ? [{key:'status'}] : []),
        ...(showCreationRange ? [{ key: 'creation_date_range', name: 'Creation Date Range' }] : []),
        ...this.tableColumns
      ]
    },
    preventRowAdd(){
      return this.selectedRows?.length === this.data.ad_groups.length
    }
  },
  methods: {
    ...mapActions('MetricsModule', ['updateLocalPresetField']),
    formatNumber(value, type, currency) {
      return String(formatNumberLabel(value, type, currency))
    },
    getKpiType(kpi) {
      return this.getMetricLookup?.[kpi.key]?.type
    },
    getRowCheckboxColor(groupBy) {
      const currentGraph = this.graphInfo.selectedGraph
      if (this.isAdLevel || currentGraph === 'grid') return '#000'
      return this.getMappedRowColor[currentGraph][groupBy]
    },
    handleCopySuccess(){
      this.$showAlert({
        type: 'success',
        message: `Copied ${this.groupedBy?.replace(/_/g, ' ')}`
      })
    },
    uniqueAds(ads){
      const adNames = ads.map((ad) => ad.ad_name);
      return _.uniq(adNames)
    },
    // memoize the color format for a given value & kpi & color format
    showColoredCell: _.memoize(function (percentDiff, kpi, outlierFormat) {
      const metricDir = this.getMetricLookup?.[kpi]?.metric_direction
      return getRelativeMetricScoreColor(percentDiff, kpi, outlierFormat, this.summaryStats, metricDir)
    }, (arg1, arg2, arg3) => `${arg1}-${arg2}-${arg3}`),
    getColoredCell(row,kpi) {
      const key = kpi.key;
      const percentDiff = Math.floor(row.percent_deviation?.[key])
      return this.showColoredCell(percentDiff, key, this.tableConfig?.colorFormat)?.scoreColor;
    },
    manageArrayAdd (arr, toAdd) {
      const exists = arr.includes(toAdd)
      return exists ? arr.filter((val) => val !== toAdd) : [...arr, toAdd]
    },
    // Column actions
    pinColumn (header) {
      const newTableCols = [...this.tableColumns].map(col => {
        if (col.key === header.key) {
          return { ...col, is_pinned: !col.is_pinned };
        }
        return col;
      });
      this.$emit('pin', newTableCols)
    },
    sortColumn (column, order) {
      const colKey = column.key

      const newSort = {
        by: colKey,
        order
      }
      const samekey = newSort.by === this.currentSort?.by;
      const sameOrder = newSort.order === this.currentSort?.order;
      if (samekey && sameOrder) return;
      // Prevent fetch on report change, as pagination will trigger a requery
      this.$emit('sort', newSort)
    },
    selectColumn (column) {
      const colKey = column.key
      const updatedColumns = this.manageArrayAdd(this.selectedColumns, colKey)
      this.$emit('update:selectedColumns', updatedColumns)
    },
    selectRow (row) {
      const rowId = row.group_by;

      const updatedRows = this.selectedRows.some(selectedRow => selectedRow.group_by === rowId)
        ? this.selectedRows.filter(selectedRow => selectedRow.group_by !== rowId)
        : [...this.selectedRows, row];
      this.$emit('update:selectedRows', updatedRows)
    },
    isRowSelected (id) {
      if (!this.selectedRows || this.selectedRows.length == 0) return;
      return this.selectedRows.findIndex((row) => row.group_by === id) !== -1;
    },
    toggleAllRows () {
      if (this.preventRowAdd) {
        console.log('%c CLEARING', 'font-size: 16px; color: green;')
        this.$emit('update:selectedRows', [])
      } else {
        console.log(`%c SETTING ALL ${this.maxRowCount}`, 'font-size: 16px; color: blue;')
        this.$emit('update:selectedRows', this.rows.slice(0, this.maxRowCount))
      }
    },
    calculatePinOffset (kpi) {
      const key = kpi.key;
      // To incrementally clip the next pinned column
      const index = this.pinnedColumns.findIndex((col) => col.key === key)
      if (index === 0) return 320

      let currentColumns = [...this.pinnedColumns]
      currentColumns = currentColumns.slice(0, index)
      return currentColumns.reduce((acc, column) => {
        const columnRef = this.$refs[`td-${column.key}`]?.[0]
        return acc + Math.ceil(columnRef?.offsetWidth || 0)
      }, 320)
    },
    getColumnColor(header){
      const key = header.key;
      const cIndex = this.selectedColumns.findIndex((col) => col === key);

      return kpiColors[cIndex];
    },
    navToNetInfo(){
      window.open("https://foreplay.featurebase.app/en/help/articles/4240094-table-net-results", "_blank")
    }
  }
}
</script>

<style scoped>
.scroll-container::-webkit-scrollbar {
  height: 16px; 
}

.scroll-container::-webkit-scrollbar-track {
  background: #ffffff;
}

.scroll-container::-webkit-scrollbar-thumb {
  background-color: rgba(236, 239, 243, 1);
  border-radius: 10px;
  border: 5px solid #ffffff;
  cursor: pointer;
  transition: border-color 150ms ease-in-out, border-width 150ms ease-in-out;  
}

.scroll-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(223, 225, 231, 1);
  border-width: 4px;
}

.scroll-container {
  transition: all 150ms;
  scroll-behavior: smooth;
}

/* Table Styles */
table {
  border-collapse: separate;
  border-spacing: 0px;
}

th,
td {
  background-color: white;
  border: none;
  margin: 0;
  padding: 0px;
}

/* Border Styles (rounded corners & bordered cells) */
table tr th:first-child .group-cell {
  border-top-left-radius: 6px;
  border: 1px solid #ECEFF3;
}

table tr th:last-child .cell-header:last-child {
  border-right: 1px solid #ECEFF3;
  border-top: 1px solid #ECEFF3;
  border-bottom: 1px solid #ECEFF3;
  border-top-right-radius: 6px;
}

table th:not(:first-child):not(:last-child) .cell-header {
  border-right: 1px solid #ECEFF3;
  border-top: 1px solid #ECEFF3;
  border-bottom: 1px solid #ECEFF3;
}

table tr td .group-cell,
table tr td .cell {
  border-bottom: 1px solid #ECEFF3;
} 

table tr td.sticky-column .cell, table tfoot tr td .cell, table tr td:last-child .cell{
  border-right: 1px solid #eceff3; 
}

table tr td:not(first-child) .group-cell {
  border-left: 1px solid #eceff3;
  border-right: 1px solid #eceff3; 
} 


table tfoot tr td:last-child .cell {
  border-bottom-right-radius: 6px;
}

table tfoot tr td .group-cell {
  border-bottom-left-radius: 6px;
}


.table-highlight {
  background-color: #fcfcfc;
}

.table-cell {
  transition: colors 50ms ease-in-out;
  z-index: 2;
}


.cell {
  min-width: 132px;
  max-width: 100%;
}




/* Base cell highlight style */
.cell-highlight{
  position: absolute;
  top: -1px;
  left: 0px;
  height: calc(100% + 1px);
  width: calc(100% + 1px);
  z-index: 2;
  pointer-events: none;
}

.cell-red >>> .table-cell-value{
  color: #710E21
}

.cell-green >>> .table-cell-value{
  color: #184E44
}

.cell-green-lowAvg{
  background-color: #EFFEFA !important;
}

.cell-green-midAvg {
  background-color: #D8F3EE !important;
}

.cell-green-highAvg {
  background-color: #B9E9E0 !important;
}

.cell-red-lowAvg{
  background-color: #FFF0F3 !important;
}

.cell-red-midAvg{
  background-color: #FDD9E0 !important;
}

.cell-red-highAvg{
  background-color: #F9C8D2 !important;
}

.group-cell {
  width: 320px;
}


.sticky-column {
  position: sticky;
  left: 0;
  z-index: 3;
}

.column-actions>div {
  transition: opacity 150ms ease-in-out;
}

.truncate-group-tooltip{
  background: #24252D;
  bottom: calc(100% - 12px);
  opacity: 0;
  z-index: 999 !important;
  transition: all 150ms;
  pointer-events:none;
  overflow: hidden;
  max-width: 700px;

  left: 0;
  transform: translateY(0);
}
.truncate-group-trigger:hover + .truncate-group-tooltip {
  transform: translateY(-14px) !important;
  opacity: 1 !important;
}

/*
  Checkbox Styles
*/
.checkbox-input {
  box-shadow: 0px 0px 0px 1px #09194821, 0px 1px 2px 0px #09194821;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: background-color 150ms ease-in-out;
}

.checkbox-input:disabled {
  background-color: #ECEFF3;
  cursor: not-allowed;
}

.checkbox-input:checked {
  background-color: black;
}
</style>
